import { Controller } from "@hotwired/stimulus"

// The walkthrough is a sequencer. The server renders the steps as an annotated
// ordered list (ol.step-list, each li[data-lines="a-b"]); without JS that list IS
// the walkthrough (CSS shows it and hides the .js-only deck). With JS the pads
// light line ranges, dim the rest via the .codeblock.stepping / .ln.lit contract,
// and print the active caption into the aria-live region; ← → advance from
// anywhere inside the figure.
//
// Controller element: the walkthrough <figure> containing a .codeblock with
// .ln[data-l] lines, .pads > .pad[data-step], [data-step-prev]/[data-step-next],
// .step-cap and ol.step-list.
//
// Audio (Monitor Bus tick on each step) is intentionally omitted — deferred.
export default class extends Controller {
  connect() {
    this.lines = Array.from(this.element.querySelectorAll(".codeblock .ln"))
    this.pads = Array.from(this.element.querySelectorAll(".pad"))
    this.cap = this.element.querySelector(".step-cap")
    const items = Array.from(this.element.querySelectorAll(".step-list li"))
    if (!this.lines.length || !this.pads.length || !this.cap || !items.length) return

    this.steps = items.map((li) => {
      const [a, b] = (li.dataset.lines || "1-1").split("-").map(Number)
      const clone = li.cloneNode(true)
      clone.querySelectorAll(".lrange").forEach((n) => n.remove()) // caption prints its own range
      return { a, b: b || a, html: clone.innerHTML.trim() }
    })

    this.codeblock = this.element.querySelector(".codeblock")
    this.codeblock?.classList.add("stepping")
    this.cur = 0

    this._onPad = this._onPad.bind(this)
    this._onPrev = this._onPrev.bind(this)
    this._onNext = this._onNext.bind(this)
    this._onKey = this._onKey.bind(this)

    this.padsEl = this.element.querySelector(".pads")
    this.prevBtn = this.element.querySelector("[data-step-prev]")
    this.nextBtn = this.element.querySelector("[data-step-next]")
    this.padsEl?.addEventListener("click", this._onPad)
    this.prevBtn?.addEventListener("click", this._onPrev)
    this.nextBtn?.addEventListener("click", this._onNext)
    this.element.addEventListener("keydown", this._onKey)

    this._go(0)
  }

  disconnect() {
    this.padsEl?.removeEventListener("click", this._onPad)
    this.prevBtn?.removeEventListener("click", this._onPrev)
    this.nextBtn?.removeEventListener("click", this._onNext)
    this.element.removeEventListener("keydown", this._onKey)
    this.codeblock?.classList.remove("stepping")
  }

  _go(i) {
    this.cur = (i + this.steps.length) % this.steps.length
    const { a, b, html } = this.steps[this.cur]
    this.lines.forEach((ln) => {
      const n = +ln.dataset.l
      ln.classList.toggle("lit", n >= a && n <= b)
    })
    this.pads.forEach((p, k) => p.setAttribute("aria-pressed", String(k === this.cur)))
    this.cap.innerHTML =
      `<b>STEP ${this.cur + 1}/${this.steps.length} · L${a}${b > a ? "–" + b : ""}</b> — ${html}`
  }

  _onPad(e) {
    const p = e.target.closest(".pad")
    if (p) this._go(+p.dataset.step)
  }

  _onPrev() { this._go(this.cur - 1) }

  _onNext() { this._go(this.cur + 1) }

  _onKey(e) {
    if (e.key === "ArrowRight") { e.preventDefault(); this._go(this.cur + 1) }
    if (e.key === "ArrowLeft") { e.preventDefault(); this._go(this.cur - 1) }
  }
}
